class ScheduleTour {
    constructor() {
        this.root = null;
        this.abortController = null;
        this.formObserver = null;
        this.flowResizeObserver = null;
        this.recaptchaRelocationTimer = null;
        this.panelHistory = [];
        this.flowHistory = ['intro'];
        this.timeSlots = {};
        this.selectedDate = null;
        this.selectedTime = null;
        this.currentAppointment = null;
        this.managementToken = null;
        this.manageMode = 'cancel';
        this.isSubmitting = false;
        this.isValidating = false;
    }

    init(context = document) {
        this.destroy();

        this.root = context.querySelector('[data-schedule-tour]');
        if (!this.root) {
            return;
        }

        this.abortController = new AbortController();
        this.signal = this.abortController.signal;
        this.propertyCode = this.root.dataset.propertyCode || '';
        this.availabilityDays = this.root.dataset.availabilityDays || '30';
        this.app = this.root.querySelector('[data-schedule-tour-app]');
        this.loader = this.root.querySelector('[data-schedule-tour-loader]');
        this.status = this.root.querySelector('[data-schedule-tour-status]');
        this.back = this.root.querySelector('[data-schedule-tour-back]');
        this.backButton = this.root.querySelector('[data-schedule-tour-back-button]');
        this.flow = this.root.querySelector('[data-schedule-tour-flow]');
        this.startButton = this.root.querySelector('[data-schedule-tour-start]');
        this.introTitle = this.root.querySelector('#schedule-tour-heading');
        this.introTitleDefault = this.introTitle?.textContent.trim() || 'Book Your Tour';
        this.calendar = this.root.querySelector('[data-schedule-tour-calendar]');
        this.calendarHeading = this.root.querySelector('[data-schedule-tour-calendar-heading]');
        this.calendarTitle = this.root.querySelector('[data-schedule-tour-calendar-title]');
        this.times = this.root.querySelector('[data-schedule-tour-times]');
        this.timeList = this.root.querySelector('[data-schedule-tour-time-list]');
        this.timesHeading = this.root.querySelector('[data-schedule-tour-times-heading]');
        this.formTitle = this.root.querySelector('[data-schedule-tour-form-title]');
        this.formWrapper = this.root.querySelector('[data-schedule-tour-form-wrapper]');
        this.form = this.formWrapper?.querySelector('[data-fui-form]');
        this.submitting = this.root.querySelector('[data-schedule-tour-submitting]');
        this.bookingError = this.root.querySelector('[data-schedule-tour-booking-error]');
        this.manageForm = this.root.querySelector('[data-schedule-tour-manage-form]');
        this.manageError = this.root.querySelector('[data-schedule-tour-manage-error]');
        this.manageRequestStatus = this.root.querySelector(
            '[data-schedule-tour-manage-request-status]'
        );
        this.manageStatus = this.root.querySelector('[data-schedule-tour-manage-status]');
        this.manageResult = this.root.querySelector('[data-schedule-tour-manage-result]');
        this.appointmentList = this.root.querySelector('[data-schedule-tour-appointment-list]');
        this.restartButton = this.root.querySelector('[data-schedule-tour-restart]');

        this.bindEvents();
        this.observeFlow();
        this.configureForm();
        this.loadAvailability({root: this.root});
        this.resolveManagementLink();
    }

    destroy() {
        this.abortController?.abort();
        this.formObserver?.disconnect();
        this.flowResizeObserver?.disconnect();
        window.clearTimeout(this.recaptchaRelocationTimer);
        this.abortController = null;
        this.formObserver = null;
        this.flowResizeObserver = null;
        this.recaptchaRelocationTimer = null;
        this.root = null;
    }

    bindEvents() {
        window.addEventListener('resize', () => {
            this.relocateRecaptchaBadge();
            this.updateBackVisibility();
        }, {signal: this.signal});
        this.backButton?.addEventListener('click', () => this.goBack(), {signal: this.signal});
        this.startButton?.addEventListener('click', () => {
            this.setFlowStep('calendar', {focus: this.calendarTitle});
        }, {signal: this.signal});

        this.root.querySelectorAll('[data-schedule-tour-action]').forEach((button) => {
            button.addEventListener('click', () => {
                this.openManagePanel(button.dataset.scheduleTourAction);
            }, {signal: this.signal});
        });

        this.calendar?.addEventListener('change', () => {
            if (this.calendar.value) {
                this.selectDate(this.calendar.value);
            }
        }, {signal: this.signal});
        this.calendar?.addEventListener('focusday', (event) => {
            this.updateCalendarHeading(event.detail);
        }, {signal: this.signal});
        this.root.querySelectorAll('[data-schedule-tour-calendar-nav]').forEach((button) => {
            button.addEventListener('click', () => {
                window.requestAnimationFrame(() => this.updateCalendarHeading());
            }, {signal: this.signal});
        });

        this.manageForm?.addEventListener('submit', (event) => {
            event.preventDefault();
            this.requestAppointmentAccess();
        }, {signal: this.signal});
        this.restartButton?.addEventListener('click', () => {
            this.restartTour();
        }, {signal: this.signal});
    }

    async loadAvailability({announce = false, root = this.root} = {}) {
        this.setBusy(true);

        try {
            const [slots] = await Promise.all([
                this.fetchSlots(),
                import('cally'),
            ]);

            if (this.root !== root) {
                return;
            }

            this.timeSlots = this.transformSlots(slots);
            await this.configureCalendar();
            this.showApplication();

            if (!Object.keys(this.timeSlots).length) {
                this.renderAvailabilityMessage(
                    'No tour appointments are currently available. Please check back later.'
                );
            } else if (announce) {
                this.announce('Appointment availability has been refreshed.');
            }
        } catch (error) {
            if (error.name === 'AbortError' || this.root !== root) {
                return;
            }

            console.error('Unable to load appointment availability.', error);
            this.showApplication();
            this.renderAvailabilityMessage(
                'We could not load appointment availability. Please refresh the page or try again later.',
                true
            );
        } finally {
            if (this.root === root) {
                this.setBusy(false);
            }
        }
    }

    async fetchSlots() {
        const url = new URL('/actions/yardi/get/appointment-slots', window.location.origin);
        url.searchParams.set('noOfDays', this.availabilityDays);

        if (this.propertyCode) {
            url.searchParams.set('propertyCode', this.propertyCode);
        }

        const response = await fetch(url, {
            headers: {'Accept': 'application/json'},
            signal: this.signal,
        });

        if (!response.ok) {
            throw new Error(`Availability request failed with status ${response.status}.`);
        }

        const data = await response.json();
        if (data?.errorCode && Number(data.errorCode) !== 0) {
            throw new Error(data.errorMessage || 'Availability request failed.');
        }

        const slots = Array.isArray(data) ? data : data?.data;
        if (!Array.isArray(slots)) {
            throw new Error('Availability response was not an array.');
        }

        return slots;
    }

    transformSlots(slots) {
        return slots.reduce((dates, slot) => {
            const start = slot.StartTime || slot.startTime || slot.start;
            const end = slot.EndTime || slot.endTime || slot.end;
            const date = this.normalizeDate(start)?.key;

            if (!date || !start) {
                return dates;
            }

            dates[date] ||= [];
            dates[date].push({start, end});
            return dates;
        }, {});
    }

    async configureCalendar() {
        if (!this.calendar) {
            return;
        }

        await customElements.whenDefined('calendar-date');

        const dates = Object.keys(this.timeSlots).sort();
        if (!dates.length) {
            this.calendar.setAttribute('aria-disabled', 'true');
            return;
        }

        this.calendar.removeAttribute('aria-disabled');
        this.calendar.min = dates[0];
        this.calendar.max = dates[dates.length - 1];
        this.calendar.focusedDate ||= dates[0];
        this.calendar.isDateDisallowed = (date) => {
            const dateKey = date.toISOString().slice(0, 10);
            return !this.timeSlots[dateKey]?.length;
        };
        this.updateCalendarHeading();
    }

    selectDate(dateValue) {
        const normalizedDate = this.normalizeDate(dateValue);
        if (!normalizedDate) {
            return;
        }

        this.selectedDate = normalizedDate.key;
        this.selectedTime = null;
        this.flow.dataset.hasSelectedTime = 'false';
        this.calendarTitle.textContent = normalizedDate.short;
        this.timesHeading.textContent = 'Select a Time';
        this.times.hidden = false;
        this.renderTimeSlots();
        if (this.flow.dataset.step === 'form') {
            this.timesHeading.focus({preventScroll: true});
            this.syncFlowHeight();
        } else {
            this.setFlowStep('calendar', {focus: this.timesHeading});
        }
        this.announce(`Available tour times for ${normalizedDate.short}.`);
    }

    renderTimeSlots(message = '') {
        const slots = this.timeSlots[this.selectedDate] || [];
        this.timeList.replaceChildren();

        if (message) {
            this.timeList.append(this.createMessage(message, true));
        }

        if (!slots.length) {
            this.timeList.append(this.createMessage('No times are available for this date.'));
            return;
        }

        slots.forEach((slot) => {
            const value = this.getSlotValue(slot);
            const button = document.createElement('button');
            button.className = 'schedule-tour-time btn';
            button.type = 'button';
            button.textContent = value.label;
            button.setAttribute('aria-pressed', 'false');
            button.addEventListener('click', () => this.selectTime(slot, button), {
                signal: this.signal,
            });
            this.timeList.append(button);
        });
    }

    selectTime(slot, selectedButton = null) {
        const value = this.getSlotValue(slot);
        const normalizedDate = this.normalizeDate(value.date);
        if (!normalizedDate) {
            return;
        }

        this.selectedDate = normalizedDate.key;
        this.selectedTime = value.apiTime;
        this.flow.dataset.hasSelectedTime = 'true';
        this.calendarTitle.textContent = `${normalizedDate.short} at ${value.label}`;
        this.timeList.querySelectorAll('.schedule-tour-time').forEach((button) => {
            const isSelected = button === selectedButton;
            button.classList.toggle('_active', isSelected);
            button.setAttribute('aria-pressed', String(isSelected));
        });
        this.setFormField('apptDate', normalizedDate.display);
        this.setFormField('apptTime', value.apiTime);
        this.setFormField('propertyCode', this.propertyCode);

        this.setFlowStep('form', {focus: this.formTitle});
    }

    configureForm() {
        if (!this.form) {
            return;
        }

        this.appointmentAction = this.form.querySelector(
            '[data-schedule-tour-appointment-action]'
        );
        if (!this.appointmentAction) {
            this.appointmentAction = document.createElement('input');
            this.appointmentAction.type = 'hidden';
            this.appointmentAction.name = 'appointmentAction';
            this.appointmentAction.dataset.scheduleTourAppointmentAction = '';
            this.form.append(this.appointmentAction);
        }
        this.setAppointmentAction('booking');

        this.removeFormieSuccess();
        this.relocateRecaptchaBadge();
        this.formObserver = new MutationObserver(() => {
            this.removeFormieSuccess();
            this.relocateRecaptchaBadge();
        });
        this.formObserver.observe(this.form.parentNode || this.form, {
            childList: true,
            subtree: true,
        });

        this.form.addEventListener('submit', (event) => this.handleFormSubmit(event), {
            capture: true,
            signal: this.signal,
        });
        this.form.addEventListener('onAfterFormieSubmit', (event) => {
            this.handleFormieResponse(event.detail || {});
        }, {signal: this.signal});
        this.form.addEventListener('onFormieSubmit', () => {
            this.syncRecaptchaResponse();
            this.setSubmitting(true);
        }, {signal: this.signal});
        this.form.addEventListener('onFormieValidateError', () => {
            this.isSubmitting = false;
            this.setFormButtonsDisabled(false);
        }, {signal: this.signal});
        this.form.addEventListener('onFormieSubmitError', () => {
            this.setSubmitting(false);
        }, {signal: this.signal});

        const moveInDate = this.form.querySelector('[name="fields[desiredMoveinDate][date]"]');
        if (moveInDate) {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            moveInDate.min = this.dateToKey(tomorrow);
        }
    }

    async handleFormSubmit(event) {
        if (this.isSubmitting || this.isValidating) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.isValidating = true;
        this.setFormButtonsDisabled(true);
        this.hideAlert(this.bookingError);

        const slotAvailable = await this.validateSelectedSlot();
        this.isValidating = false;

        if (!slotAvailable) {
            this.setFormButtonsDisabled(false);
            return;
        }

        if (this.form.form?.initSubmit) {
            this.form.form.initSubmit();
        } else {
            this.showAlert(
                this.bookingError,
                'The appointment form is still loading. Please try again.'
            );
            this.setFormButtonsDisabled(false);
        }
    }

    async validateSelectedSlot() {
        if (!this.selectedDate || !this.selectedTime) {
            this.showAlert(this.bookingError, 'Select an appointment time before submitting.');
            return false;
        }

        try {
            this.timeSlots = this.transformSlots(await this.fetchSlots());
            await this.configureCalendar();
        } catch (error) {
            this.showAlert(
                this.bookingError,
                'We could not confirm that appointment time. Please try again.'
            );
            return false;
        }

        if (!this.selectedSlotIsAvailable()) {
            this.renderTimeSlots('That appointment time is no longer available. Choose another time.');
            this.showPanel('calendar', {resetHistory: true});
            this.setFlowStep('calendar', {replace: true, focus: this.timesHeading});
            this.announce('That appointment time is no longer available.');
            return false;
        }

        return true;
    }

    async handleFormieResponse(data) {
        this.isValidating = false;

        const submissionId = data?.submissionId ?? data?.id ?? null;
        if (!data?.success && !submissionId) {
            this.setSubmitting(false);
            const message = data?.message
                || data?.error
                || 'We could not book that appointment. Please choose another time.';

            try {
                this.timeSlots = this.transformSlots(await this.fetchSlots());
                await this.configureCalendar();
            } catch (error) {
                this.showAlert(this.bookingError, message);
                return;
            }

            if (!this.selectedSlotIsAvailable()) {
                this.renderTimeSlots(
                    'That appointment time is no longer available. Choose another time.'
                );
                this.showPanel('calendar', {resetHistory: true});
                this.setFlowStep('calendar', {replace: true, focus: this.timesHeading});
                this.announce('That appointment time is no longer available.');
            } else {
                this.showAlert(this.bookingError, message);
            }
            return;
        }

        const submission = await this.getSubmission({submissionId});
        const appointment = submission?.[0] || null;
        const details = this.root.querySelector('[data-schedule-tour-confirmation-details]');
        this.renderAppointment(details, appointment);
        this.showPanel('confirmation', {resetHistory: true});
        this.setSubmitting(false);
        this.dispatchCompletion(this.manageMode === 'reschedule' ? 'rescheduled' : 'booked', {
            submissionId,
            currentAppointment: appointment,
        });
        this.currentAppointment = null;
        this.manageMode = 'cancel';
        this.loadAvailability();
    }

    openManagePanel(mode) {
        this.manageMode = mode === 'reschedule' ? 'reschedule' : 'cancel';
        this.resetManagePanel();

        const title = this.root.querySelector('[data-schedule-tour-manage-title]');
        const copy = this.root.querySelector('[data-schedule-tour-manage-copy]');
        title.textContent = this.manageMode === 'reschedule' ? 'Reschedule Your Tour' : 'Cancel Your Tour';
        copy.textContent =
            'Enter the email address used to schedule your tour. We will send a secure link to manage any active appointments.';

        this.showPanel('manage');
    }

    async requestAppointmentAccess() {
        const submitButton = this.manageForm.querySelector('[type="submit"]');
        const formData = new FormData(this.manageForm);
        formData.set('propertyCode', this.propertyCode);
        formData.set('mode', this.manageMode);
        formData.set('manageUrl', `${window.location.origin}${window.location.pathname}`);
        await this.addCsrfToken(formData);

        this.hideAlert(this.manageError);
        this.manageResult.hidden = true;
        this.manageStatus.hidden = true;
        this.manageRequestStatus.hidden = true;
        submitButton.disabled = true;
        this.announce('Requesting a secure appointment management link.');

        try {
            const response = await fetch(
                '/actions/formie-yardi-integration/appointments/request-access',
                {
                    method: 'POST',
                    headers: {'Accept': 'application/json'},
                    body: formData,
                    signal: this.signal,
                }
            );
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Unable to request appointment access.');
            }

            this.manageRequestStatus.replaceChildren(
                this.createMessage(data.message)
            );
            this.manageRequestStatus.hidden = false;
            this.announce(data.message);
        } catch (error) {
            console.error('Unable to request appointment access.', error);
            this.showAlert(
                this.manageError,
                error.message || 'We could not send your secure link. Please try again.'
            );
        } finally {
            submitButton.disabled = false;
        }
    }

    async resolveManagementLink() {
        const url = new URL(window.location.href);
        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
        const token = hashParams.get('manage');
        if (!token) {
            return;
        }

        try {
            const body = new FormData();
            await this.addCsrfToken(body);
            body.set('token', token);
            const response = await fetch(
                '/actions/formie-yardi-integration/appointments/resolve',
                {
                method: 'POST',
                headers: {'Accept': 'application/json'},
                body,
                signal: this.signal,
                }
            );
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Unable to verify appointment access.');
            }

            this.openManagePanel(data.mode);
            this.managementToken = token;
            this.renderManagedAppointments(data.appointments || []);
        } catch (error) {
            this.openManagePanel('cancel');
            this.showAlert(
                this.manageError,
                error.message || 'This appointment management link is invalid or has expired.'
            );
        } finally {
            hashParams.delete('manage');
            const hash = hashParams.toString();
            window.history.replaceState(
                {},
                '',
                `${url.pathname}${url.search}${hash ? `#${hash}` : ''}`
            );
        }
    }

    renderManagedAppointments(appointments) {
        this.manageRequestStatus.hidden = true;
        this.manageStatus.hidden = false;
        this.manageResult.hidden = false;
        this.appointmentList.replaceChildren();
        this.root.querySelector('[data-schedule-tour-result-heading]').textContent =
            this.manageMode === 'reschedule' ? 'Choose an Appointment to Reschedule' : 'Choose an Appointment to Cancel';

        if (!appointments.length) {
            this.appointmentList.append(
                this.createMessage('No active appointments were found for this email address.')
            );
            return;
        }

        appointments.forEach((appointment) => {
            const item = document.createElement('article');
            const details = document.createElement('div');
            const button = document.createElement('button');
            item.className = 'schedule-tour-appointment';
            button.className = 'btn btn-theme';
            button.type = 'button';
            button.textContent = this.manageMode === 'reschedule'
                ? 'Reschedule'
                : 'Confirm cancellation';
            button.addEventListener('click', () => {
                this.cancelAppointment(appointment, button);
            }, {signal: this.signal});
            this.renderAppointment(details, {
                apptDate: appointment.date,
                apptTime: appointment.time,
            });
            item.append(details, button);
            this.appointmentList.append(item);
        });

        this.appointmentList.querySelector('button')?.focus({preventScroll: true});
        this.announce(`${appointments.length} active appointment${appointments.length === 1 ? '' : 's'} found.`);
    }

    async cancelAppointment(appointment, button) {
        if (!this.managementToken || !appointment?.key) {
            return;
        }

        const originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = this.manageMode === 'reschedule'
            ? 'Cancelling appointment…'
            : 'Cancelling…';
        this.announce('Cancelling your appointment.');

        try {
            const body = new FormData();
            await this.addCsrfToken(body);
            body.set('token', this.managementToken);
            body.set('appointmentKey', appointment.key);

            const response = await fetch(
                '/actions/formie-yardi-integration/appointments/cancel',
                {
                    method: 'POST',
                    headers: {'Accept': 'application/json'},
                    body,
                    signal: this.signal,
                }
            );
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Cancellation was not confirmed.');
            }

            const action = this.manageMode === 'reschedule' ? 'rescheduled' : 'cancelled';
            const cancelledAppointment = {
                apptDate: data.appointment?.date || appointment.date,
                apptTime: data.appointment?.time || appointment.time,
            };

            if (this.manageMode === 'reschedule') {
                this.resetCalendarSelection();
                this.setAppointmentAction('reschedule');
                this.introTitle.textContent = 'Schedule a New Appointment';
                await this.loadAvailability({announce: true, root: this.root});
                this.showPanel('calendar', {resetHistory: true});
                this.announce('Your previous appointment was cancelled. Choose a new tour time.');
            } else {
                this.renderAppointment(
                    this.root.querySelector('[data-schedule-tour-cancelled-details]'),
                    cancelledAppointment,
                    {inline: true}
                );
                this.showPanel('cancelled', {resetHistory: true});
            }

            this.dispatchCompletion(action, {
                currentAppointment: cancelledAppointment,
            });

            if (this.manageMode !== 'reschedule') {
                this.currentAppointment = null;
            }
        } catch (error) {
            console.error('Unable to cancel appointment.', error);
            this.showAlert(
                this.manageError,
                error.message || 'We could not cancel your appointment. It has not been changed. Please try again.'
            );
        } finally {
            button.disabled = false;
            button.textContent = originalLabel;
        }
    }

    async getSubmission({submissionId = null}) {
        try {
            const url = new URL(
                '/actions/formie-yardi-integration/get/submissions',
                window.location.origin
            );
            url.searchParams.set('formHandle', 'yardiAppointmentForm');

            if (submissionId) url.searchParams.set('id', submissionId);

            const response = await fetch(url, {
                headers: {'Accept': 'application/json'},
                signal: this.signal,
            });
            if (!response.ok) {
                throw new Error(`Appointment lookup failed with status ${response.status}.`);
            }

            const data = await response.json();
            return Array.isArray(data) ? data : data?.data || [];
        } catch (error) {
            console.error('Unable to look up appointment.', error);
            return null;
        }
    }

    async addCsrfToken(formData) {
        const csrf = this.manageForm?.querySelector(
            'input[name="CRAFT_CSRF_TOKEN"]'
        );
        if (csrf) {
            formData.set(csrf.name, csrf.value);
            return;
        }

        const response = await fetch('/index.php/actions/users/session-info', {
            headers: {'Accept': 'application/json'},
            credentials: 'same-origin',
            signal: this.signal,
        });
        if (!response.ok) {
            throw new Error('Unable to initialize the secure request.');
        }

        const data = await response.json();
        if (!data.csrfTokenName || !data.csrfTokenValue) {
            throw new Error('Unable to initialize the secure request.');
        }
        formData.set(data.csrfTokenName, data.csrfTokenValue);
    }

    showPanel(name, {resetHistory = false} = {}) {
        const current = this.root.querySelector('[data-schedule-tour-panel]:not([hidden])');
        const next = this.root.querySelector(`[data-schedule-tour-panel="${name}"]`);
        if (!next || current === next) {
            return;
        }

        if (resetHistory) {
            this.panelHistory = [];
        } else if (current) {
            this.panelHistory.push(current.dataset.scheduleTourPanel);
        }

        this.root.querySelectorAll('[data-schedule-tour-panel]').forEach((panel) => {
            panel.hidden = panel !== next;
        });
        this.updateBackVisibility();

        const heading = next.querySelector('[data-schedule-tour-focus]');
        window.requestAnimationFrame(() => heading?.focus({preventScroll: true}));
        this.root.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    goBack() {
        const currentPanel = this.root.querySelector('[data-schedule-tour-panel]:not([hidden])');
        if (
            currentPanel?.dataset.scheduleTourPanel === 'calendar'
            && this.flowHistory.length > 1
        ) {
            this.flowHistory.pop();
            const previousStep = this.flowHistory.at(-1);
            this.setFlowStep(previousStep, {
                replace: true,
                focus: this.getFlowHeading(previousStep),
            });
            return;
        }

        const previous = this.panelHistory.pop();
        if (!previous) {
            return;
        }

        const panel = this.root.querySelector(`[data-schedule-tour-panel="${previous}"]`);
        if (!panel) {
            return;
        }

        this.root.querySelectorAll('[data-schedule-tour-panel]').forEach((item) => {
            item.hidden = item !== panel;
        });
        this.updateBackVisibility();
        window.requestAnimationFrame(() => {
            panel.querySelector('[data-schedule-tour-focus]')?.focus({preventScroll: true});
        });
    }

    resetManagePanel() {
        this.manageForm?.reset();
        this.manageResult.hidden = true;
        this.manageStatus.hidden = true;
        this.manageRequestStatus.hidden = true;
        this.manageRequestStatus.replaceChildren();
        this.appointmentList?.replaceChildren();
        this.hideAlert(this.manageError);
        this.currentAppointment = null;
        this.managementToken = null;
    }

    async restartTour() {
        this.resetManagePanel();
        this.resetCalendarSelection();
        this.introTitle.textContent = this.introTitleDefault;
        this.manageMode = 'cancel';
        this.currentAppointment = null;
        this.managementToken = null;
        this.setAppointmentAction('booking');
        this.showPanel('calendar', {resetHistory: true});
        await this.loadAvailability({root: this.root});
        this.announce('Schedule another tour.');
    }

    resetCalendarSelection() {
        this.selectedDate = null;
        this.selectedTime = null;
        this.flow.dataset.hasSelectedTime = 'false';
        this.calendar.value = '';
        this.calendarTitle.textContent = 'Select a Date';
        this.timesHeading.textContent = 'Select a Time';
        this.times.hidden = true;
        this.timeList.replaceChildren();
        this.timeList.append(this.createMessage('Select a date to view available times.'));
        this.setFlowStep('intro', {reset: true});
    }

    setSubmitting(isSubmitting) {
        this.isSubmitting = isSubmitting;
        this.formWrapper.hidden = isSubmitting;
        this.submitting.hidden = !isSubmitting;
        this.setFormButtonsDisabled(isSubmitting);
        if (isSubmitting) {
            this.announce('Submitting your appointment request.');
            if (!window.matchMedia('(min-width: 768px)').matches) {
                window.requestAnimationFrame(() => {
                    this.formTitle?.closest('.schedule-tour-form')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                });
            }
        }
    }

    setAppointmentAction(action) {
        if (this.appointmentAction) {
            this.appointmentAction.value = action === 'reschedule' ? 'reschedule' : 'booking';
        }
    }

    setFormButtonsDisabled(disabled) {
        this.form?.querySelectorAll('[type="submit"]').forEach((button) => {
            button.disabled = disabled;
        });
    }

    setFormField(handle, value) {
        const field = this.form?.querySelector(`[name="fields[${handle}]"]`);
        if (!field) {
            return;
        }
        field.value = value || '';
        field.dispatchEvent(new Event('input', {bubbles: true}));
        field.dispatchEvent(new Event('change', {bubbles: true}));
    }

    prefillBookingForm() {
        if (!this.currentAppointment || !this.form) {
            return;
        }

        const excluded = new Set([
            'id',
            'apptDate',
            'apptTime',
            'voyagerProspectCode',
            'voyagerProspectId',
            'voyagerProspectAppointmentId',
        ]);

        Object.entries(this.currentAppointment).forEach(([handle, value]) => {
            if (excluded.has(handle) || value == null || typeof value === 'object') {
                return;
            }
            this.setFormField(handle, value);
        });
    }

    removeFormieSuccess() {
        this.root?.querySelectorAll(
            '.fui-alert-success, .fui-success-message, .fui-success'
        ).forEach((element) => element.remove());
    }

    relocateRecaptchaBadge() {
        window.clearTimeout(this.recaptchaRelocationTimer);
        this.recaptchaRelocationTimer = window.setTimeout(() => {
            this.root?.querySelectorAll('.grecaptcha-badge').forEach((badge) => {
                document.body.append(badge);
            });
        }, 150);
    }

    syncRecaptchaResponse() {
        const badgeResponse = document.querySelector(
            '.grecaptcha-badge [name="g-recaptcha-response"]'
        );
        let value = badgeResponse?.value || '';

        if (!value && window.grecaptcha?.getResponse) {
            try {
                value = window.grecaptcha.getResponse() || '';
            } catch (error) {
                value = '';
            }
        }

        let formResponse = this.form.querySelector(
            '[data-schedule-tour-recaptcha-response]'
        );
        if (!formResponse) {
            formResponse = document.createElement('input');
            formResponse.type = 'hidden';
            formResponse.name = 'g-recaptcha-response';
            formResponse.dataset.scheduleTourRecaptchaResponse = '';
            this.form.append(formResponse);
        }
        formResponse.value = value;
    }

    renderAppointment(container, appointment, {inline = false} = {}) {
        container.replaceChildren();
        if (!appointment) {
            container.append(this.createMessage(
                'Your appointment was saved. Confirmation details will be sent by email.'
            ));
            return;
        }

        const name = [appointment.firstName, appointment.lastName].filter(Boolean).join(' ');
        const date = this.normalizeDate(appointment.apptDate);
        const time = this.formatAppointmentTime(appointment.apptTime);

        if (container.matches('[data-schedule-tour-confirmation-details]')) {
            container.classList.add('schedule-tour-confirmation-details');
            [
                name,
                appointment.email,
                date?.short,
                time,
            ].filter(Boolean).forEach((value) => {
                const line = document.createElement('p');
                line.textContent = value;
                container.append(line);
            });
            return;
        }

        const summary = document.createElement(inline ? 'span' : 'p');
        summary.textContent = [
            name,
            date?.short,
            time ? `at ${time}` : null,
        ].filter(Boolean).join(' ');

        container.append(summary);
    }

    formatAppointmentTime(value) {
        const match = String(value || '').match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!match) {
            return String(value || '');
        }

        return `${Number(match[1])}:${match[2]} ${match[3].toUpperCase()}`;
    }

    renderAvailabilityMessage(message, isError = false) {
        this.timeList.replaceChildren(this.createMessage(message, isError));
        this.announce(message);
    }

    setFlowStep(step, {focus = null, replace = false, reset = false} = {}) {
        if (!this.flow) {
            return;
        }

        const current = this.flow.dataset.step || 'intro';
        if (reset) {
            this.flowHistory = [step];
        } else if (replace) {
            if (this.flowHistory.length) {
                this.flowHistory[this.flowHistory.length - 1] = step;
            } else {
                this.flowHistory = [step];
            }
        } else if (current !== step) {
            this.flowHistory.push(step);
        }

        this.flow.dataset.step = step;
        this.updateBackVisibility();
        if (step === 'form') {
            this.relocateRecaptchaBadge();
        }
        window.requestAnimationFrame(() => {
            this.syncFlowHeight();
            focus?.focus({preventScroll: true});
        });
    }

    getFlowHeading(step) {
        return {
            intro: this.root.querySelector('#schedule-tour-heading'),
            calendar: this.calendarTitle,
            times: this.timesHeading,
            form: this.formTitle,
        }[step] || null;
    }

    observeFlow() {
        if (!this.flow) {
            return;
        }

        this.flowResizeObserver = new ResizeObserver(() => this.syncFlowHeight());
        this.root.querySelectorAll('[data-schedule-tour-column]').forEach((column) => {
            this.flowResizeObserver.observe(column);
        });
        window.addEventListener('resize', () => this.syncFlowHeight(), {signal: this.signal});
    }

    syncFlowHeight() {
        if (!this.flow) {
            return;
        }

        this.flow.style.removeProperty('height');
    }

    updateBackVisibility() {
        if (!this.back) {
            return;
        }

        const hasDesktopFlowHistory =
            window.matchMedia('(min-width: 768px)').matches &&
            this.flowHistory.length > 1;
        const isVisible = this.panelHistory.length > 0 || hasDesktopFlowHistory;
        this.back.classList.toggle('_active', isVisible);
        this.back.setAttribute('aria-hidden', String(!isVisible));
        this.backButton.disabled = !isVisible;
    }

    updateCalendarHeading(value = this.calendar?.focusedDate) {
        if (!this.calendarHeading) {
            return;
        }

        const normalizedDate = this.normalizeDate(value);
        if (!normalizedDate) {
            return;
        }

        this.calendarHeading.textContent = new Intl.DateTimeFormat('en-US', {
            month: 'long',
            year: 'numeric',
        }).format(normalizedDate.date);
    }

    createMessage(message, isError = false) {
        const element = document.createElement('p');
        element.className = isError ? 'schedule-tour-alert' : '';
        element.textContent = message;
        return element;
    }

    showAlert(element, message) {
        element.textContent = message;
        element.hidden = false;
        element.focus?.({preventScroll: true});
        this.announce(message);
    }

    hideAlert(element) {
        if (!element) return;
        element.hidden = true;
        element.textContent = '';
    }

    showApplication() {
        this.loader.hidden = true;
        this.app.hidden = false;
        window.requestAnimationFrame(() => this.syncFlowHeight());
    }

    setBusy(isBusy) {
        this.root?.querySelector('.schedule-tour')?.setAttribute('aria-busy', String(isBusy));
    }

    announce(message) {
        if (!this.status) return;
        this.status.textContent = '';
        window.requestAnimationFrame(() => {
            this.status.textContent = message;
        });
    }

    getSlotValue(slot) {
        const raw = String(slot.start || '').trim();
        const timeMatch = raw.match(/(\d{1,2}:\d{2})\s*(AM|PM)/i);
        const date = this.normalizeDate(raw)?.key;
        const apiTime = timeMatch
            ? `${timeMatch[1]}${timeMatch[2].toUpperCase()}`
            : raw.split(/\s+/).slice(1).join('').toUpperCase();
        const parsed = this.parseSlotDate(raw);
        const label = parsed
            ? new Intl.DateTimeFormat('en-US', {hour: 'numeric', minute: '2-digit'}).format(parsed)
            : apiTime;

        return {date, apiTime, label};
    }

    selectedSlotIsAvailable() {
        return (this.timeSlots[this.selectedDate] || []).some((slot) => {
            return this.getSlotValue(slot).apiTime === this.selectedTime;
        });
    }

    parseSlotDate(value) {
        const normalized = String(value).replace(
            /^(\d{2})\/(\d{2})\/(\d{4})/,
            '$3-$1-$2'
        );
        const date = new Date(normalized);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    normalizeDate(value) {
        if (!value) {
            return null;
        }

        const stringValue = value instanceof Date
            ? this.dateToKey(value)
            : String(value).trim();
        const iso = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        const us = stringValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);

        const year = Number(iso?.[1] || us?.[3]);
        const month = Number(iso?.[2] || us?.[1]);
        const day = Number(iso?.[3] || us?.[2]);
        if (!year || !month || !day) {
            return null;
        }

        const date = new Date(year, month - 1, day);
        const key = this.dateToKey(date);
        const weekday = ['Sun', 'Mon', 'Tues', 'Weds', 'Thurs', 'Fri', 'Sat'][date.getDay()];
        const shortMonth = new Intl.DateTimeFormat('en-US', {month: 'short'}).format(date);
        return {
            key,
            date,
            display: `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`,
            short: `${weekday}, ${shortMonth} ${day}`,
        };
    }

    dateToKey(date) {
        return [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, '0'),
            String(date.getDate()).padStart(2, '0'),
        ].join('-');
    }

    wasCancelled(data) {
        return data?.Cancelled === true
            || data?.Cancelled === 'true'
            || data?.cancelled === true
            || data?.cancelled === 'true'
            || Number(data?.StatusCode || data?.statusCode) === 200;
    }

    dispatchCompletion(action, detail = {}) {
        const eventDetail = {
            action,
            isReschedule: action === 'rescheduled',
            selectedDate: this.selectedDate,
            selectedTime: this.selectedTime,
            ...detail,
        };

        this.root.dispatchEvent(new CustomEvent('yardi:appointment-completed', {
            bubbles: true,
            detail: eventDetail,
        }));
        this.root.dispatchEvent(new CustomEvent(`yardi:appointment-${action}`, {
            bubbles: true,
            detail: eventDetail,
        }));
    }
}

export default new ScheduleTour();
